import {
  CalendarCheck,
  ChevronLeft,
  FileText,
  Globe,
  History,
  MapPin,
  Pencil,
  Scale,
  Swords,
  Tag,
} from "lucide-react";
import {
  useGetOne,
  useLocaleState,
  useNotify,
  useTranslate,
  useUpdate,
} from "ra-core";
import { useState, type ReactNode } from "react";
import { Link, useParams } from "react-router";

import {
  EmptyState,
  ExternalAnchor,
  LoadingCards,
  SectionLabel,
} from "@/components/market-intel/components";
import { formatShortDate, toIsoDay } from "@/components/market-intel/dates";
import { MiniMarkdown } from "@/components/market-intel/MiniMarkdown";
import type { MarketCompetitor } from "@/components/market-intel/types";
import { hostOf, riseStyle } from "@/components/market-intel/util";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

import { ActivitySection } from "./ActivitySection";
import { CompetitorAvatar } from "./CompetitorAvatar";
import { CompetitorForm } from "./CompetitorForm";
import { CompetitorTimeline } from "./CompetitorTimeline";
import { LineBadge } from "./CompetitorsView";
import { ProductsSection } from "./ProductsSection";
import { COMPETITORS_BASE } from "./routes";
import { SocialProfilesSection } from "./SocialProfilesSection";
import { TrackingStatusMenu } from "./TrackingStatus";
import { RelatedTrends } from "./TrendsView";
import { SectionCard } from "./ui";

/** Anchors of the phone's section pills (ids of the section cards). */
const SECTIONS = [
  { id: "ficha-analisis", label: "competition.detail.analysis" },
  { id: "ficha-redes", label: "competition.social.heading" },
  { id: "ficha-actividad", label: "competition.activity.heading" },
  { id: "ficha-precios", label: "competition.products.heading" },
  { id: "ficha-resumen", label: "competition.detail.summary" },
  { id: "ficha-cronologia", label: "competition.detail.timeline" },
  { id: "ficha-tendencias", label: "competition.detail.related_trends" },
] as const;

/**
 * A competitor's full profile: header (logo, web, location, entry price,
 * tracking status), strengths / weaknesses / conclusion, social profiles with
 * their dated metrics, marketing activity, prices, summary, timeline and the
 * related trends. Two columns from `lg`; stacked glass cards (with jump
 * pills) on a phone.
 */
export const CompetitorShow = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const { id = "" } = useParams();
  const [editing, setEditing] = useState(false);
  const {
    data: competitor,
    isPending,
    error,
  } = useGetOne<MarketCompetitor>("market_competitors", { id });

  // The phone's top bar already has a Back chevron.
  const back = isMobile ? null : (
    <Link
      to={COMPETITORS_BASE}
      className="liquid-glass glass-press inline-flex h-9 items-center gap-1 rounded-full pr-4 pl-2.5 text-sm font-semibold text-[color:var(--glass-foreground)]"
    >
      <ChevronLeft aria-hidden className="size-5" />
      {translate("competition.detail.back")}
    </Link>
  );

  if (isPending) return <LoadingCards count={2} />;
  if (error || !competitor)
    return (
      <div className="space-y-4">
        {back}
        <EmptyState
          icon={Swords}
          title={translate("market.competitors.not_found")}
        />
      </div>
    );

  const editButton = (
    <Button
      type="button"
      variant="ghost"
      onClick={() => setEditing(true)}
      className="liquid-glass glass-press h-10 shrink-0 rounded-full px-4 text-sm font-semibold text-[color:var(--glass-foreground)] md:h-9"
    >
      <Pencil aria-hidden className="size-4" />
      {translate("market.competitors.edit")}
    </Button>
  );

  return (
    <div className="space-y-4 pb-4">
      {isMobile ? null : (
        <div className="flex items-center justify-between gap-2">
          {back}
          {editButton}
        </div>
      )}
      <CompetitorHeader
        competitor={competitor}
        action={isMobile ? editButton : null}
      />
      {isMobile ? <SectionPills /> : null}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-start">
        <div className="flex min-w-0 flex-col gap-4">
          <AnalysisCard competitor={competitor} />
          <SocialProfilesSection
            id="ficha-redes"
            competitorId={competitor.id}
          />
          <ActivitySection id="ficha-actividad" competitorId={competitor.id} />
        </div>
        <div className="flex min-w-0 flex-col gap-4">
          <ProductsSection id="ficha-precios" competitor={competitor} />
          <SummaryCard competitor={competitor} />
          <SectionCard
            id="ficha-cronologia"
            icon={History}
            title={translate("competition.detail.timeline")}
            style={riseStyle(5)}
          >
            <CompetitorTimeline competitorId={competitor.id} />
          </SectionCard>
          <RelatedTrends id="ficha-tendencias" competitorId={competitor.id} />
        </div>
      </div>
      <CompetitorForm
        open={editing}
        onOpenChange={setEditing}
        competitor={competitor}
      />
    </div>
  );
};

const SectionPills = () => {
  const translate = useTranslate();
  const jump = (id: string) =>
    document.getElementById(id)?.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
      block: "start",
    });
  return (
    <nav
      aria-label={translate("competition.detail.sections")}
      className="mi-tabs-sticky"
    >
      <div className="mi-pills">
        {SECTIONS.map((section) => (
          <a
            key={section.id}
            href={`#${section.id}`}
            onClick={(event) => {
              event.preventDefault();
              jump(section.id);
            }}
            className="mi-pill liquid-glass inline-flex items-center outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
          >
            {translate(section.label)}
          </a>
        ))}
      </div>
    </nav>
  );
};

const CompetitorHeader = ({
  competitor,
  action,
}: {
  competitor: MarketCompetitor;
  action: ReactNode;
}) => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const [update, { isPending }] = useUpdate<MarketCompetitor>();
  const notify = useNotify();
  const today = toIsoDay(new Date());

  const markReviewed = () =>
    update(
      "market_competitors",
      {
        id: competitor.id,
        data: { last_reviewed_at: today },
        previousData: competitor,
      },
      {
        mutationMode: "optimistic",
        onSuccess: () =>
          notify("market.competitors.updated", { type: "success" }),
        onError: () => notify("market.competitors.error", { type: "error" }),
      },
    );

  return (
    <article className="mi-card mi-rise flex min-w-0 flex-col gap-3 p-4 md:p-5">
      <div className="flex min-w-0 items-start gap-3.5">
        <CompetitorAvatar competitor={competitor} size="lg" />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <h2
              id="mi-competitor-title"
              className="min-w-0 pt-0.5 text-xl leading-tight font-semibold tracking-tight wrap-anywhere md:text-2xl"
            >
              {competitor.name}
            </h2>
            {action}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <LineBadge line={competitor.line} />
            <TrackingStatusMenu competitor={competitor} />
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm">
        {competitor.entry_price ? (
          <span className="inline-flex min-w-0 items-center gap-1.5 font-semibold">
            <Tag aria-hidden className="size-4 shrink-0 text-primary" />
            <span className="sr-only">
              {translate("market.competitors.entry_price")}:{" "}
            </span>
            <span className="wrap-anywhere">{competitor.entry_price}</span>
          </span>
        ) : null}
        {competitor.location ? (
          <span className="inline-flex min-w-0 items-center gap-1 text-muted-foreground">
            <MapPin aria-hidden className="size-4 shrink-0" />
            <span className="sr-only">
              {translate("market.competitors.location")}:{" "}
            </span>
            <span className="truncate">{competitor.location}</span>
          </span>
        ) : null}
        {competitor.website ? (
          <ExternalAnchor href={competitor.website}>
            <Globe aria-hidden className="mr-1 inline size-3.5" />
            {hostOf(competitor.website)}
          </ExternalAnchor>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3">
        <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
          <CalendarCheck aria-hidden className="size-4" />
          {competitor.last_reviewed_at
            ? translate("market.competitors.last_reviewed", {
                date: formatShortDate(competitor.last_reviewed_at, locale),
              })
            : translate("market.competitors.never_reviewed")}
        </span>
        {competitor.last_reviewed_at !== today ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={markReviewed}
            disabled={isPending}
            className="h-8 rounded-full px-3 text-xs font-semibold text-primary"
          >
            {translate("market.competitors.mark_reviewed")}
          </Button>
        ) : null}
      </div>
    </article>
  );
};

/** Strengths / weaknesses side by side, the conclusion highlighted below. */
const AnalysisCard = ({ competitor }: { competitor: MarketCompetitor }) => {
  const translate = useTranslate();
  const empty =
    !competitor.strengths && !competitor.weaknesses && !competitor.conclusion;
  return (
    <SectionCard
      id="ficha-analisis"
      icon={Scale}
      title={translate("competition.detail.analysis")}
      style={riseStyle(1)}
    >
      {empty ? (
        <p className="text-sm text-muted-foreground">
          {translate("competition.detail.no_analysis")}
        </p>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {competitor.strengths ? (
              <Verdict
                tone="good"
                label={translate("competition.detail.strengths")}
              >
                <MiniMarkdown
                  source={competitor.strengths}
                  className="text-sm"
                />
              </Verdict>
            ) : null}
            {competitor.weaknesses ? (
              <Verdict
                tone="bad"
                label={translate("competition.detail.weaknesses")}
              >
                <MiniMarkdown
                  source={competitor.weaknesses}
                  className="text-sm"
                />
              </Verdict>
            ) : null}
          </div>
          {competitor.conclusion ? (
            <div className="rounded-2xl bg-primary/8 p-3.5 ring-1 ring-primary/15 ring-inset dark:bg-primary/12 dark:ring-primary/25">
              <SectionLabel>
                {translate("competition.detail.conclusion")}
              </SectionLabel>
              <MiniMarkdown
                source={competitor.conclusion}
                className="text-[15px] font-medium"
              />
            </div>
          ) : null}
        </div>
      )}
    </SectionCard>
  );
};

const Verdict = ({
  tone,
  label,
  children,
}: {
  tone: "good" | "bad";
  label: string;
  children: ReactNode;
}) => (
  <div
    data-tone={tone}
    className={cn(
      "min-w-0 rounded-2xl border-l-[3px] bg-muted/50 py-2.5 pr-3 pl-3 dark:bg-white/5",
      tone === "good"
        ? "border-emerald-500 dark:border-emerald-400"
        : "border-red-500 dark:border-red-400",
    )}
  >
    <SectionLabel>{label}</SectionLabel>
    {children}
  </div>
);

const SummaryCard = ({ competitor }: { competitor: MarketCompetitor }) => {
  const translate = useTranslate();
  const rows: [string, ReactNode][] = [];
  if (competitor.value_proposition)
    rows.push([
      translate("market.competitors.value_proposition"),
      <p className="text-[15px] wrap-anywhere">
        {competitor.value_proposition}
      </p>,
    ]);
  if (competitor.description)
    rows.push([
      translate("market.competitors.what_they_do"),
      <MiniMarkdown source={competitor.description} />,
    ]);
  if (competitor.channels)
    rows.push([
      translate("market.competitors.channels"),
      <p className="text-sm wrap-anywhere">{competitor.channels}</p>,
    ]);
  if (competitor.pricing)
    rows.push([
      translate("market.competitors.pricing"),
      <p className="text-sm wrap-anywhere">{competitor.pricing}</p>,
    ]);
  if (competitor.notes)
    rows.push([
      translate("market.competitors.notes"),
      <MiniMarkdown source={competitor.notes} className="text-sm" />,
    ]);
  return (
    <SectionCard
      id="ficha-resumen"
      icon={FileText}
      title={translate("competition.detail.summary")}
      style={riseStyle(4)}
    >
      {rows.length ? (
        <div className="space-y-3">
          {rows.map(([label, content]) => (
            <div key={label} className="min-w-0">
              <SectionLabel>{label}</SectionLabel>
              {content}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          {translate("competition.detail.no_summary")}
        </p>
      )}
    </SectionCard>
  );
};
